import "@emotion/react"

const colors = {
  black: "#000000",
  white: "#ffffff",
  boulder: "#7A7A7A",
  silver: "#cdcdcd",
  silverChalice: "#ADADAD",
  silverChalice2: "#A7A7A7",
  gray: "#939393",
  gray2: "#828282",
  gray3: "#e6e6e6",
  seashell: "#f1f1f1",
  lightGray: "#f0f0f0",
  lightGray2: "#eaeaea",
  alto: "#DEDEDE",
  dustyGray: "#989898",
  gallery: "#EBEBEB",
  codGray: "#141414",
  // New colors
  // backgrounds
  balticSea: "#1C1B1F",
  dawnPink: "#EFE7DE",
  springWood: "#F8F4F0",
  // actions
  dodgerBlue: "#286fff",
  royalBlue: "#3b58e3",
  royalBlue200: "#C2D3FB",
  royalBlue600: "#3B58E3",
  dodgerBlueLight: "#E3EEFB",
  perfume: "#D8B8FB",
  perfumeLight: "#F4EAFF",
  oliveGreen: "#9BB454",
  bilbao: "#157F1F",
  oliveGreenLight: "#E8F0C6",
  wheat: "#F4D9B0",
  wheatLight: "#FFECCF",
  brown200: "#F7F5F3",
  gray300: "#F4F4F4",
  gray400: "#E9E8E9",
  gray500: "#D2D2D3",
  gray600: "#BCBBBD",
  gray700: "#8F8D90",
  gray800: "#626064",
  gray900: "#1F1C22",
  blue700: "#3B58E3",
  tahunaSands: "#E8F0C6",
  green800: "#157F1F",
  green900: "#063715",
  cornflowerBlue: "#6E84EC",
  periwinkle: "#BECBFD",
  periwinkle2: "#C9D5FF",
  periwinkle3: "#bdcbfd",
  tabasco: "#A82C10",
  zumthor: "#E3E9FF",
  zircon: "#ECF0FF",
  chateauGreen: "#46A459",
  raisin50: "#F5F4F6",
  raisin100: "#EBE9ED",
  raisin200: "#E0DDE3",
  raisin300: "#D5D2DA",
  raisin700: "#47404F",
  raisin950: "#1F1C22",
  grayscale100: "#F4F4F5",
  grayscale200: "#EAEAEC",
  grayscale300: "#DFDFE2",
  grayscale400: "#E9E8E9",
  grayscale500: "#9F9FA8",
  grayscale600: "#747481",
  grayscale700: "#575760",
  grayscale800: "#626064",
  grayscale900: "#1F1C22",
  grayscale950: "#131315",
  royalBlue50: "#F0F4FE",
  royalBlue100: "#DDE6FC",
  royalBlue500: "#446CED",
  spring50: "#DEF7E4",
  spring100: "#ADEBBC",
  spring500: "#208339",
  spring600: "#1C7332",
  cinnabar: "#E33B3B",
} as const

const functionalColors = {
  text: {
    primary: colors.black,
    secondary: colors.boulder,
    tertiary: colors.silverChalice,
    tertiary2: colors.silverChalice2,
    tertiary3: colors.gray800,
    tertiary4: colors.gray900,
    quaternary: colors.white,
    ...colors,
  },
  button: {
    primary: {
      backgroundColor: colors.black,
      textColor: colors.white,
    },
    primary3D: {
      outerBackgroundColor: "#1C38C4",
      innerBackgroundColor: colors.blue700,
      textColor: colors.white,
      disabledBackgroundColor: "#9eaaf1",
    },
    secondary: {
      backgroundColor: colors.white,
      textColor: colors.black,
      borderColor: colors.black,
    },
    secondary3D: {
      outerBackgroundColor: colors.black,
      innerBackgroundColor: colors.white,
      textColor: colors.black,
      borderColor: colors.black,
    },
    tertiary3D: {
      outerBackgroundColor: colors.gray500,
      innerBackgroundColor: colors.white,
      textColor: colors.gray800,
      borderColor: colors.gray500,
    },
    bilbao3D: {
      outerBackgroundColor: "#0E5C16",
      innerBackgroundColor: colors.bilbao,
      textColor: colors.white,
      disabledBackgroundColor: "#9eaaf1",
    },
    white23D: {
      outerBackgroundColor: "rgba(255, 255, 255, 0.7)",
      innerBackgroundColor: colors.white,
      textColor: colors.bilbao,
      disabledBackgroundColor: "#9eaaf1",
    },
    white33D: {
      outerBackgroundColor: colors.black,
      innerBackgroundColor: colors.white,
      textColor: colors.bilbao,
      disabledBackgroundColor: "#9eaaf1",
    },
    filter: {
      inactive: {
        borderColor: colors.grayscale200,
        backgroundColor: colors.white,
        shadowColor: colors.alto,
      },
      active: {
        borderColor: colors.dodgerBlue,
        backgroundColor: colors.grayscale950,
        shadowColor: colors.dodgerBlueLight,
      },
    },
    pay: {
      backgroundColor: colors.royalBlue50,
      textColor: colors.royalBlue,
    },
  },
  card: {
    backgroundColor: colors.lightGray,
    borderColor: colors.lightGray2,
    darkerBorderColor: colors.silver,
  },
  dateSelector: {
    backgroundColor: colors.gray3,
  },
  header: {
    backgroundColor: colors.seashell,
  },
  transactionList: {
    leftIconBackgroundColor: colors.grayscale200,
  },
  transferDetailsScreen: {
    backgroundColor: colors.gray900,
    refundButtonOuterColor: colors.spring500,
    refundButtonTextColor: colors.white,
    detailLine: colors.gray400,
    closeIconContainer: "rgba(140, 227, 162, 0.5)",
    pendingReimbursementBorderColor: colors.gray800,
  },
  lisItem: {
    backgroundColor: colors.seashell,
  },
  textInput: {
    labelColor: colors.silverChalice2,
    textColor: colors.gray900,
    backgroundColor: colors.gray400,
  },
  phoneNumberInput: {
    backgroundColor: colors.gray400,
    countryCodeSeparatorBorderColor: colors.gray600,
    inputColor: colors.gray900,
  },
  otpBox: {
    backgroundColor: colors.gray400,
    focusedBorderColor: colors.gray600,
    textColor: colors.gray900,
  },
  tabs: {
    activeLabelColor: colors.black,
    inactiveLabelColor: colors.gray2,
    iconInactiveColor: colors.gray2,
    iconActiveColor: colors.black,
    payIconColor: colors.dodgerBlue,
    borderTopColor: colors.gray400,
  },
  paymentsTabs: {
    activeLabelColor: colors.dodgerBlue,
    inactiveLabelColor: colors.gray2,
    iconInactiveColor: colors.gray2,
    iconActiveColor: colors.dodgerBlue,
  },
  paymentList: {
    leftIconBackgroundColor: colors.grayscale200,
    tabViewBorderBottomColor: colors.grayscale400,
    reimbursementCaretRightColor: colors.grayscale500,
  },
  homeScreen: {
    iconBorderColor: colors.lightGray2,
    reimbursementsContainer: colors.spring50,
    missingServiceBorder: colors.grayscale300,
    potentialClaimsUpperBorder: colors.spring100,
  },
  profileScreen: {
    headerBackgroundColor: colors.white,
    screenBackgroundColor: colors.raisin50,
    backArrowBackgroundColor: colors.raisin200,
    optionsOuterBackgroundColor: colors.raisin300,
    optionsBorderColor: colors.raisin300,
    optionBorderColor: colors.raisin300,
  },
  accountScreen: {
    headerColor: colors.seashell,
    settingsIconBackgroundColor: colors.alto,
    sectionHeaderTextColor: colors.dustyGray,
    autoPayContainerColor: colors.royalBlue50,
    listItemLogoColor: colors.grayscale200,
    backgroundColor: colors.raisin50,
    addAccountColor: colors.raisin100,
  },
  personalDetails: {
    calendarIconColor: colors.dustyGray,
  },
  filtersSheet: {
    closeContainerColor: colors.gallery,
    labelColor: colors.codGray,
    headerSeparatorColor: colors.grayscale200,
  },
  iconsColor: {
    refunded: colors.oliveGreen,
  },
  backArrow: {
    background: colors.brown200,
  },
  paymentRequestScreen: {
    accountTileShadow: colors.gray500,
    accountTileBackground: colors.white,
    amountWrapperBorder: colors.gray500,
    amountColor: colors.bilbao,
    radioButtonColor: colors.bilbao,
    balanceBackgroundColor: colors.royalBlue50,
    balanceBackgroundColorInsufficient: "#FEF0F0",
    saverBackgroundColor: colors.royalBlue100,
    buttonContainerColor: colors.royalBlue100,
    buttonContainerBorderColor: colors.royalBlue200,
    debitAuthBackgroundColor: colors.grayscale100,
  },
  debitAuthPayment: {
    autoPayIconBg: colors.royalBlue50,
  },
  paymentSentSuccessScreen: {
    backgroundColor: colors.bilbao,
  },
  chooseInsuranceScreen: {
    upperDeckBorderColor: colors.gray400,
    searchBarPlaceholderColor: colors.gray700,
  },
  reimbursementTabScreen: {
    name: colors.white,
    line: colors.gray800,
    label: colors.gray400,
    unfocusedDot: colors.gray500,
    claimBadge: colors.gray400,
    focusedDot: colors.blue700,
    refundedIcon: colors.green800,
  },
  businessProfile: {
    backgroundColor: colors.gray500,
    allPaymentsWrapperColor: colors.white,
    allPaymentsTextColor: colors.gray900,
    mapContainerBorder: colors.gray400,
  },
  errorScreen: {
    closeIconContainer: colors.gray300,
  },
  recipientsDetailsScreen: {
    autoPayIconContainerColor: colors.grayscale200,
  },
  autoPay: {
    listItemBackgroundColor: colors.grayscale100,
    modalIconColor: colors.grayscale600,
  },
  v2: {
    components: {
      buttons: {
        blue: {
          backgroundColor: colors.cornflowerBlue,
          textColor: "white",
        },
        blue2: {
          backgroundColor: colors.royalBlue,
          textColor: "white",
        },
        blue3: {
          backgroundColor: colors.periwinkle,
          textColor: "white",
        },
        white: {
          backgroundColor: colors.white,
          textColor: colors.gray900,
        },
        zumthor: {
          backgroundColor: colors.zumthor,
          textColor: colors.gray900,
        },
      },
      onboardingWrapper: {
        backArrowContainerColor: "rgba(190, 203, 253, 0.33)",
        progressStepColor: colors.periwinkle2,
        progressStepActiveColor: colors.royalBlue,
      },
      inputErrorMessage: {
        textColor: colors.tabasco,
      },
      accordion: {
        lineColor: colors.gray400,
      },
    },
    screens: {
      initial: {
        backgroundColor: colors.gray900,
      },
      terms: {
        buttonContainerBorderColor: colors.gray400,
      },
      reimbursements: {
        claimsContainerBackgroundColor: colors.zircon,
        addProviderButtonBackgroundColor: colors.periwinkle3,
        refundedIcon: colors.tahunaSands,
        refundedIconContainerColor: colors.gray900,
      },
      insuranceConnection: {
        progressBarFilledColor: colors.chateauGreen,
        progressBarUnfilledColor: colors.gray500,
        progressBarEnabledButtonColor: colors.royalBlue,
        progressBarDisabledButtonColor: colors.gray400,
        magnifyingGlassContainerColor: colors.gray900,
      },
    },
  },
  charityFlow: {
    calculator: {
      donateToBorderColor: colors.royalBlue200,
      donateToTextColor: colors.raisin700,
    },
  },
} as const

export type FunctionalColors = typeof functionalColors

declare module "@emotion/react" {
  // Interface merge with Emotion's Theme (do not use `type Theme = …` — duplicates their interface).
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- Emotion module augmentation
  export interface Theme extends FunctionalColors {}
}

export { functionalColors }
